import { setDoc, Timestamp } from 'firebase/firestore';
import { userCollectionBagDoc } from '../firebase/collections';
import { countedGetDoc } from '../data/firestoreReads';
import type { CollectionEntry } from '../types';
import {
  clampLotsToQuantities,
  parseLots,
  serializeLots,
} from './collectionLots';

export const MAX_COLLECTION_BAG_BYTES = 900_000;
export const COLLECTION_BAG_VERSION = 1;

export type CollectionBagRow = {
  q: number;
  f: number;
  u: number;
  d?: ReturnType<typeof serializeLots>;
};

export function emptyCollectionEntries(): Record<string, CollectionEntry> {
  return {};
}

function toMillis(value: unknown, fallback = Date.now()): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value && typeof value === 'object' && 'toMillis' in value) {
    const toMillisFn = (value as { toMillis?: () => number }).toMillis;
    if (typeof toMillisFn === 'function') return toMillisFn.call(value);
  }
  return fallback;
}

export function parseCollectionBag(raw: unknown): Record<string, CollectionEntry> | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  if (!data.entries || typeof data.entries !== 'object' || Array.isArray(data.entries)) {
    return null;
  }
  const notesRaw =
    data.notes && typeof data.notes === 'object' && !Array.isArray(data.notes)
      ? (data.notes as Record<string, unknown>)
      : {};
  const now = Date.now();
  const entries: Record<string, CollectionEntry> = {};
  for (const [cardId, row] of Object.entries(data.entries as Record<string, unknown>)) {
    if (!cardId || !row || typeof row !== 'object') continue;
    const rec = row as Record<string, unknown>;
    const quantity = Math.max(0, Math.floor(Number(rec.q) || 0));
    if (!quantity) continue;
    const quantityFoil = Math.min(Math.max(0, Math.floor(Number(rec.f) || 0)), quantity);
    const updatedAt = toMillis(rec.u, now);
    const note = notesRaw[cardId];
    const lots = parseLots(rec.d, quantity, quantityFoil);
    entries[cardId] = {
      cardId,
      quantity,
      quantityFoil,
      notes: typeof note === 'string' && note ? note : undefined,
      lots,
      addedAt: updatedAt,
      updatedAt,
    };
  }
  return entries;
}

export function collectionBagToFirestore(
  entries: Record<string, CollectionEntry>,
  revision = 1,
): Record<string, unknown> {
  const bagEntries: Record<string, CollectionBagRow> = {};
  const notes: Record<string, string> = {};
  for (const [cardId, entry] of Object.entries(entries)) {
    if (!entry.quantity) continue;
    const quantity = entry.quantity;
    const quantityFoil = Math.min(Math.max(0, entry.quantityFoil ?? 0), quantity);
    const lots = clampLotsToQuantities(entry.lots, quantity, quantityFoil);
    const row: CollectionBagRow = {
      q: quantity,
      f: quantityFoil,
      u: entry.updatedAt || Date.now(),
    };
    const serializedLots = serializeLots(lots);
    if (serializedLots) row.d = serializedLots;
    bagEntries[cardId] = row;
    if (entry.notes?.trim()) notes[cardId] = entry.notes;
  }
  const payload: Record<string, unknown> = {
    v: COLLECTION_BAG_VERSION,
    revision,
    updatedAt: Timestamp.now(),
    entries: bagEntries,
  };
  if (Object.keys(notes).length > 0) payload.notes = notes;
  return payload;
}

export function assertCollectionBagSize(payload: Record<string, unknown>): void {
  const approx = JSON.stringify(payload).length;
  if (approx > MAX_COLLECTION_BAG_BYTES) {
    throw new Error('Collection is too large to save. Remove some cards and try again.');
  }
}

export function setCollectionBagEntry(
  entries: Record<string, CollectionEntry>,
  cardId: string,
  next: Pick<CollectionEntry, 'quantity' | 'quantityFoil'> & {
    notes?: string;
    lots?: CollectionEntry['lots'];
  },
  now = Date.now(),
): Record<string, CollectionEntry> {
  const copy = { ...entries };
  if (next.quantity <= 0) {
    delete copy[cardId];
    return copy;
  }
  const previous = entries[cardId];
  const quantity = next.quantity;
  const quantityFoil = Math.min(Math.max(0, next.quantityFoil), quantity);
  const lots = clampLotsToQuantities(
    next.lots !== undefined ? next.lots : previous?.lots,
    quantity,
    quantityFoil,
  );
  copy[cardId] = {
    cardId,
    quantity,
    quantityFoil,
    notes: next.notes !== undefined ? next.notes : previous?.notes,
    lots,
    addedAt: previous?.addedAt ?? now,
    updatedAt: now,
  };
  return copy;
}

/** Owned quantities for one card, read from a bag snapshot inside a transaction. */
export function ownedQuantitiesFromSnaps(
  bagData: unknown | undefined,
  cardId: string,
): { quantity: number; quantityFoil: number } {
  const entry = parseCollectionBag(bagData)?.[cardId];
  return { quantity: entry?.quantity ?? 0, quantityFoil: entry?.quantityFoil ?? 0 };
}

/** Bag only. Does not list leftover per-card docs. */
export async function readCollectionEntries(
  uid: string,
): Promise<Record<string, CollectionEntry>> {
  const bagSnap = await countedGetDoc(userCollectionBagDoc(uid));
  if (!bagSnap.exists()) return {};
  return parseCollectionBag(bagSnap.data()) ?? {};
}

/** One card from the bag. Does not read leftover per-card docs. */
export async function readOwnedCardEntry(
  uid: string,
  cardId: string,
): Promise<CollectionEntry | undefined> {
  const entries = await readCollectionEntries(uid);
  return entries[cardId];
}

export async function writeCollectionBag(
  uid: string,
  entries: Record<string, CollectionEntry>,
  revision: number,
): Promise<void> {
  const payload = collectionBagToFirestore(entries, revision);
  assertCollectionBagSize(payload);
  await setDoc(userCollectionBagDoc(uid), payload);
}
